'use client'
import { useElementWidth } from "@/hooks/useElementWidth";
import { createPortal } from "react-dom";
import { IOperator } from "@/modules/driver/domain/IOperator";
import { cn } from "@/utils/cn";
import { Button, Title } from "@umahealth/occipital";
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
	Icon,
	Input,
	Select,
	SelectItem,
} from "@umahealth/occipital/client";
import { differenceInYears } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useFormContext, UseFormReturn } from "react-hook-form";
import * as yup from "yup";
import { IUserBase } from "@/modules/users/domain/IUser";
import { BaseCombobox } from "../BaseCombobox";
import { createUserHandleMutationError } from "@/modules/users/infraestructure/createUser/useCreateUser";
import { revalidateGetOperators } from "@/modules/driver/infraestructure/server/getDrivers";
import { IBase } from "@umahealth/entities";
import {
	LIMITE_INICIAL,
	LIMITE_MAXIMO,
	MIN_CARACTERES_BUSQUEDA,
	PASO_DE_LIMITE,
	useSearchOperators,
} from "@/modules/driver/app/hooks/useSearchOperators";

export type gender = "F" | "M" | "X";

export const sexOptions: {
	label: string;
	value: gender;
}[] = [
	{
		label: "Masculino",
		value: "M",
	},
	{
		label: "Femenino",
		value: "F",
	},
	{
		label: "No binarie",
		value: "X",
	},
];

export type documentType = "DNI" | "Pasaporte" | "CI" | "LE" | "LC";

export const dniOptions: {
	label: string;
	value: documentType;
}[] = [
	{
		label: "DNI",
		value: "DNI",
	},
	{
		label: "Pasaporte",
		value: "Pasaporte",
	},
	{
		label: "L.C",
		value: "LC",
	},
	{
		label: "L.E",
		value: "LE",
	},
	{
		label: "C.I",
		value: "CI",
	},
];

/**
 * Funciones disponibles para la carga de empleados.
 *
 * Listado unificado de 19 funciones definido por el cliente (Alberto, OPS EMER
 * 31/08/2026 — Excel "Modificaciones UMA.xlsx", hoja Funciones). Reemplaza el
 * listado anterior de cargos sueltos.
 *
 * El backend mapea cada función a su categoría del modelo oficial de caratula
 * (ver CARATULA_MENSUAL_CATEGORY_POSITIONS en supervision.service.ts): las
 * etiquetas de la caratula difieren en redacción, así que al agregar o
 * renombrar una función hay que actualizar ese mapeo en el mismo cambio.
 */
export const positionOptions: {
	label: string;
	value: string;
}[] = [
	{ label: "Conductor", value: "Conductor" },
	{ label: "Ayudante / Preconductor / Conductor", value: "Ayudante / Preconductor / Conductor" },
	{ label: "Guardas", value: "Guardas" },
	{ label: "Material Rodante", value: "Material Rodante" },
	{ label: "Infraestructura", value: "Infraestructura" },
	{ label: "Boleterías", value: "Boleterías" },
	{ label: "Limpieza", value: "Limpieza" },
	{ label: "Evasión", value: "Evasión" },
	{ label: "Seguridad", value: "Seguridad" },
	{ label: "Cambista", value: "Cambista" },
	{ label: "Jefe de Turno", value: "Jefe de Turno" },
	{ label: "Puesto de Control de Trenes", value: "Puesto de Control de Trenes" },
	{ label: "Asist. al Pasajero", value: "Asist. al Pasajero" },
	{ label: "GPN / Guarda Paso a Nivel", value: "GPN / Guarda Paso a Nivel" },
	{ label: "Señaleros", value: "Señaleros" },
	{ label: "Auxiliar", value: "Auxiliar" },
	{ label: "Administrativos", value: "Administrativos" },
	{ label: "Personal de a bordo", value: "Personal de a bordo" },
	{ label: "Otro Cargo", value: "Otro Cargo" },
];

/**
 * Validation schema for a user form
 */
export const operatorValidationSchema = yup.object().shape({
	/**
	 * First name of the user
	 */
	firstName: yup.string().required("El nombre es requerido"),
	/**
	 * Last name of the user
	 */
	lastName: yup.string().required("El apellido es requerido"),
	/**
	 * Type of document
	 * @enum {string}
	 */
	documentType: yup
		.string()
		.oneOf(["DNI", "PASAPORTE", "L.C", "L.E", "C.I"])
		.required("El tipo de documento es requerido"),
	/**
	 * Document number
	 */
	documentNumber: yup
		.string()
		.required("El número de documento es requerido")
		.matches(/^\d+$/, "El número de documento debe contener solo números positivos"),
	/**
	 * Date of birth of the user
	 * Must be at least 18 years old
	 */
	Dob: yup
		.date()
		.required("La fecha de nacimiento es requerida")
		.test("age", "Debe tener más de 18 años", function (value) {
			return differenceInYears(new Date(), new Date(value)) >= 18;
		}),
	/**
	 * Sex of the user
	 * @enum {string}
	 */
	Sex: yup
		.string()
		.oneOf(["F", "M", "X"])
		.required("El sexo del documento es requerido"),
	/**
	 * Place of work
	 */
	PlaceOfWork: yup.number().required("La base es requerida"),
	/**
	 * Job position
	 */
	Position: yup.string().required("La posición es requerida"),
	Legajo: yup.number().positive("El legajo debe ser un número positivo").required("El legajo es requerido"),
});

export type AddOperatorFormValues = yup.InferType<
	typeof operatorValidationSchema
>;

export function AddOperatorForm({
	bases,
	onSubmit,
}: {
	bases: IUserBase[];
	onSubmit?: SubmitHandler<AddOperatorFormValues>;
}) {
	const {
		register,
		control,
		handleSubmit,
		watch,
		setValue: formSetValue,
		formState: { errors },
	} = useFormContext <AddOperatorFormValues>();

	const selectedDocumentType = watch("documentType");
	const isDNI = selectedDocumentType === "DNI";


	return (
		<form
			id="addOperatorForm"
			className="overflow-scroll flex flex-col h-[60vh] pb-12 px-8"
			onSubmit={onSubmit ? handleSubmit(onSubmit) : undefined}
		>
			<fieldset>
				<legend>Información general</legend>
				<Input
					required
					inputMode="text"
					type="text"
					autoComplete="name"
					label="Nombre"
					placeholder="Ingrese nombre"
					{...register("firstName")}
					error={errors.firstName?.message}
				/>
				<Input
					required
					inputMode="text"
					type="text"
					autoComplete="family-name"
					label="Apellido"
					placeholder="Ingrese Apellido"
					{...register("lastName")}
					error={errors.lastName?.message}
				/>
				<Input
					required
					autoComplete="off"
					inputMode="numeric"
					type="number"
					min={0}
					label="Número de legajo"
					placeholder="1441"
					{...register("Legajo")}
					error={errors.Legajo?.message}
				/>
				<Controller
					control={control}
					name="documentType"
					render={({ field }) => (
						<Select
							label="Escoga el tipo de documento"
							contentClassName="z-[50]"
							placeholder="Escoga el tipo de documento"
							className="mt-6"
							defaultValue={selectedDocumentType ?? "DNI"}
							onValueChange={(value) => field.onChange(value)}
							value={field.value}
							name={field.name}
							required
						>
							{dniOptions.map(({ label, value }) => (
								<SelectItem value={value} key={value}>
									{label}
								</SelectItem>
							))}
						</Select>
					)}
				/>

				<Input
					required
					inputMode={isDNI ? "numeric" : "text"}
					type={isDNI ? "number" : "text"}
					min={isDNI ? 0 : undefined}
					label="Número de documento"
					placeholder=""
					autoComplete="on"
					{...register("documentNumber")}
					error={errors.documentNumber?.message}
				/>
				<Input
					required
					inputMode="text"
					type="date"
					label="Fecha de nacimiento"
					autoComplete="bday"
					{...register("Dob")}
					error={errors.Dob?.message}
				/>
				<Controller
					control={control}
					name="Sex"
					render={({ field }) => (
						<Select
							label="Sexo en el documento"
							contentClassName="z-[50]"
							placeholder="Escoga el sexo que figura en su documento"
							className="mt-6"
							error={errors.Sex?.message}
							onValueChange={(value) => field.onChange(value)}
							value={field.value}
							name={field.name}
							required
						>
							{sexOptions.map(({ value, label }) => (
								<SelectItem value={value} key={value}>
									{label}
								</SelectItem>
							))}
						</Select>
					)}
				/>
			</fieldset>
			<fieldset className="mt-4">
				<legend>Sobre su actividad</legend>
				<BaseCombobox
					inputLabel="Lugar de trabajo"
					labelOutside={false}
					bases={bases}
					onValueChange={(baseId) => {
						formSetValue("PlaceOfWork", Number(baseId));
					}}
				/>
				<Controller
					control={control}
					name="Position"
					render={({ field }) => (
						<Select
							label="Puesto de trabajo"
							contentClassName="z-[50] max-h-[300px]"
							placeholder="Seleccione el puesto de trabajo"
							className="mt-6"
							error={errors.Position?.message}
							onValueChange={(value) => field.onChange(value)}
							value={field.value}
							name={field.name}
							required
						>
							{positionOptions.map(({ value, label }) => (
								<SelectItem value={value} key={value}>
									{label}
								</SelectItem>
							))}
						</Select>
					)}
				/>
			</fieldset>
		</form>
	);
}

export function OperatorCombobox({
	onValueChange,
	onOperatorSelected,
	open,
	error,
	bases,
	onSubmitNewOperator,
	isSubmitting = false,
}: {
	open?: boolean;
	onValueChange?: (value: string) => void;
	/** El operador elegido, o null si lo que hay escrito no es una selección. */
	onOperatorSelected?: (operator: IOperator | null) => void;
	error?: string;
	bases: IUserBase[];
	onSubmitNewOperator?: SubmitHandler<AddOperatorFormValues>;
	isSubmitting?: boolean;
}) {
	/** separo value de label para mostrar en el input el label */
	const [label, setLabel] = useState<string | null>(null);
	/**
	 * Ya se eligió un operador de la lista.
	 *
	 * Sin esto el desplegable quedaba abierto después de elegir —la lista se
	 * mostraba con `label ? undefined : "sr-only"`, y elegir justamente llena
	 * `label`— tapando la pregunta siguiente del formulario. En una tablet eso
	 * es media pantalla ocupada por una lista de la que ya no se necesita nada.
	 *
	 * Es el mismo patrón que usa el buscador de medicamentos.
	 */
	const [yaEligio, setYaEligio] = useState(false);
	const [elementRef, width] = useElementWidth();

	/**
	 * El desplegable se dibuja en un portal, fuera del `ScrollArea`.
	 *
	 * `OperationCardScrollArea` envuelve este paso en un `ScrollArea` cuya raíz
	 * es `relative overflow-hidden`, y la lista era `absolute`: un elemento
	 * posicionado **se recorta** contra un ancestro que oculta el desborde, así
	 * que la lista salía cortada y su propia barra de scroll quedaba fuera del
	 * área visible. El `overflow-visible` del div interno no lo deshace: el
	 * recorte pasa más arriba.
	 *
	 * Es el tercer lugar con este mismo problema —el modal de base y el
	 * desplegable de roles fueron los otros dos— y se resuelve igual: sacarlo
	 * del contenedor que recorta y posicionarlo desde la caja del input.
	 *
	 * Al portalear, la lista deja de ser descendiente del input, así que
	 * `focus-within` ya no la alcanza: la visibilidad pasa a estado, y se cierra
	 * por clic afuera o Escape, no por blur. Cerrar en `blur` escondería la
	 * lista antes de registrar el clic sobre un resultado.
	 */
	const [abierto, setAbierto] = useState(false);
	const listaRef = useRef<HTMLDivElement>(null);
	const [caja, setCaja] = useState<{
		top: number;
		left: number;
		width: number;
	} | null>(null);

	const medir = useCallback(() => {
		const rect = elementRef.current?.getBoundingClientRect();
		if (rect) {
			setCaja({ top: rect.bottom + 4, left: rect.left, width: rect.width });
		}
	}, [elementRef]);

	const mostrarLista = !yaEligio && (abierto || !!open);

	useEffect(() => {
		if (!mostrarLista) return;
		medir();
		// `true` para capturar el scroll del ScrollArea, que no burbujea.
		window.addEventListener("scroll", medir, true);
		window.addEventListener("resize", medir);
		return () => {
			window.removeEventListener("scroll", medir, true);
			window.removeEventListener("resize", medir);
		};
	}, [mostrarLista, medir]);

	useEffect(() => {
		if (!mostrarLista) return;
		const clicAfuera = (evento: MouseEvent) => {
			const objetivo = evento.target as Node;
			const adentro =
				elementRef.current?.contains(objetivo) ||
				listaRef.current?.contains(objetivo);
			if (!adentro) setAbierto(false);
		};
		const escape = (evento: KeyboardEvent) => {
			if (evento.key === "Escape") setAbierto(false);
		};
		document.addEventListener("mousedown", clicAfuera);
		document.addEventListener("keydown", escape);
		return () => {
			document.removeEventListener("mousedown", clicAfuera);
			document.removeEventListener("keydown", escape);
		};
	}, [mostrarLista, elementRef]);

	/**
	 * La búsqueda la resuelve la base.
	 *
	 * Antes el componente recibía el padrón entero y filtraba en el navegador:
	 * renderizaba un CommandItem por persona y, por cada uno, corría matchSorter
	 * sobre la lista completa. Con 122 operadores no se notaba; con los ~19.500
	 * de la nómina de Trenes son del orden de 380 millones de comparaciones por
	 * tecla apretada, sobre una tablet.
	 */
	const [limite, setLimite] = useState(LIMITE_INICIAL);
	const busqueda = useSearchOperators(label ?? "", limite);
	const operators = busqueda.operators;

	/**
	 * Elegir a alguien de la lista.
	 *
	 * Avisa las dos cosas por separado: el uid para el formulario y el
	 * operador entero para que quien lo usa sepa que hubo una selección real y
	 * no texto tipeado. Antes eso se deducía buscando el nombre en el padrón
	 * completo que el formulario tenía en memoria; desde que la búsqueda vive
	 * en el servidor ese padrón ya no está, y sin este aviso la selección se
	 * perdía y el formulario decía "Seleccione un operador del listado".
	 */
	const elegirOperador = (operator: IOperator) => {
		const fullName = `${operator.firstName.trim()} ${operator.lastName.trim()}`;
		onValueChange?.(operator.uid);
		onOperatorSelected?.(operator);
		setLabel(fullName);
		setYaEligio(true);
	};
	const sobrantes = Math.max(busqueda.total - operators.length, 0);
	const puedeVerMas = sobrantes > 0 && limite < LIMITE_MAXIMO;

	/**
	 * `null` cuando el combobox se usa fuera de un formulario.
	 *
	 * En el alta de operación esto siempre existe —`CreateOperationForm` lo
	 * envuelve en `FormProvider`— pero el buscador de operadores sirve solo, sin
	 * formulario alrededor: la carga de protocolos en papel lo usa así.
	 * Desreferenciarlo sin preguntar rompía el render del servidor entero con
	 * `Cannot read properties of null (reading 'formState')`, y la pantalla no
	 * cargaba.
	 */
	const operationForm = useFormContext();

	return (
		<div>
			<label htmlFor="buscarOperador">
				¿Quién se realizará la evaluación?
			</label>
			<Command
				ref={elementRef}
				className="rounded-lg group"
				/**
				 * El filtrado ya lo hizo la base: lo que llega es el resultado.
				 * Volver a filtrar acá escondería coincidencias que el servidor
				 * sí encontró, por ejemplo un acento que el cliente no normaliza
				 * igual.
				 */
				shouldFilter={false}
			>
				<CommandInput
					required
					id="buscarOperador"
					data-cy="buscarOperador"
					value={label ?? undefined}
					setValue={setLabel}
					onValueChange={(newValue) => {
						setLabel(newValue);
						// Volver a escribir reabre la lista: la selección anterior
						// ya no vale.
						setYaEligio(false);
						setAbierto(true);
						// Cada búsqueda nueva arranca de cero: si no, quedaría
						// pidiendo 500 resultados para siempre.
						setLimite(LIMITE_INICIAL);
						const operatorSelected = operators.find(
							(operator) =>
								`${operator.firstName.trim()} ${operator.lastName.trim()}` ===
								newValue
						);
						onOperatorSelected?.(operatorSelected ?? null);
						if (onValueChange) {
							onValueChange(operatorSelected?.uid ?? newValue);
						}
					}}
					placeholder="Buscar por número de legajo o apellido"
					className="border-0"
					onFocus={() => setAbierto(true)}
				/>
				{mostrarLista && caja && createPortal(
					<div
						ref={listaRef}
						className="fixed z-[100]"
						style={{ top: caja.top, left: caja.left, width: caja.width }}
					>
					{/*
					  * Más alto que los 200px que trae `CommandList` de occipital: con
					  * items de dos líneas —nombre y legajo— ahí entraban tres
					  * resultados y el resto quedaba detrás del scroll. Buscando por
					  * nombre común eso es casi siempre insuficiente.
					  *
					  * Se puede sobrescribir porque el componente pasa el `className`
					  * por `cn()`, que usa tailwind-merge y deduplica la clase en
					  * conflicto en lugar de dejar las dos peleando.
					  *
					  * El tope en `50vh` es por la tablet en horizontal: con una altura
					  * fija de 380px la lista tapaba el botón de continuar. Ahora que
					  * está en un portal, además, la altura la manda ella y no el
					  * contenedor que la recortaba.
					  */}
					<CommandList
						className="max-h-[min(380px,50vh)] shadow-lg border border-grey-200"
						style={{ width: `${caja.width}px` }}
					>
						{(label ?? "").trim().length < MIN_CARACTERES_BUSQUEDA && (
							<div className="px-3 py-3 text-sm text-grey-500">
								Escribí al menos {MIN_CARACTERES_BUSQUEDA} letras del
								apellido, o el número de legajo.
							</div>
						)}

						{busqueda.buscando && (
							<div className="px-3 py-3 text-sm text-grey-500">
								Buscando…
							</div>
						)}

						{busqueda.error && !busqueda.buscando && (
							<div className="px-3 py-3 text-sm text-error-600">
								No pudimos buscar. Reintentá en unos segundos.
							</div>
						)}

						{!busqueda.buscando &&
							!busqueda.error &&
							operators.length === 0 &&
							(label ?? "").trim().length >= MIN_CARACTERES_BUSQUEDA && (
								<div className="flex align-middle justify-start items-center px-3 py-2">
									<Icon
										className="mr-2.5"
										name="search"
										size="size-6"
										color="text-grey-500"
									/>
									<div className="flex flex-col justify-start text-left">
										No encontramos resultados para {label}
										{/*
										  * `DrawerTrigger` necesita el `Drawer` que lo
										  * contenga, y sin formulario no hay ninguno.
										  * Sin este gate, buscar algo rompía la pantalla
										  * entera: la lista se dibuja recién al escribir,
										  * así que el error aparecía al buscar y no al
										  * entrar.
										  */}
										{operationForm && (
											<DrawerTrigger>
												<Button
													className="text-left w-max pl-0 pt-0 h-min text-primary-700"
													type="button"
													align="start"
													variant="text"
												>
													{" "}
													Cargar manualmente{" "}
												</Button>
											</DrawerTrigger>
										)}
									</div>
								</div>
							)}
					{operators.map((operator) => {
						const fullName = `${operator.firstName.trim()} ${operator.lastName.trim()}`;
						return (
							<CommandItem
								key={operator.uid}
								value={fullName}
								// Los dos: cmdk dispara onSelect al elegir con el
								// teclado y onClick al tocar con el dedo.
								onSelect={() => elegirOperador(operator)}
								onClick={() => elegirOperador(operator)}
							>
								<div className="flex flex-col text-left justify-start">
									{fullName}
										<span className="text-xxs text-grey-600">
											{" "}
											N° Legajo: {operator.nomina}{" "}
										</span>
									</div>
								</CommandItem>
							);
						})}

						{/* Con 19.500 personas un apellido común no entra en el
						    límite. Decirlo es la diferencia entre afinar la
						    búsqueda y creer que la persona no está cargada. */}
						{/* Cargar a mano tiene que estar también cuando hay
						    resultados: puede que ninguno sea la persona. Antes
						    sólo aparecía con la lista vacía. */}
						{operators.length > 0 && operationForm && (
							<div className="px-3 py-2 border-t border-grey-100">
								<DrawerTrigger>
									<Button
										className="text-left w-max pl-0 pt-0 h-min text-primary-700 text-sm"
										type="button"
										align="start"
										variant="text"
									>
										¿No es ninguno? Cargar manualmente
									</Button>
								</DrawerTrigger>
							</div>
						)}

						{sobrantes > 0 && (
							<div className="px-3 py-2 text-xs text-grey-500 border-t border-grey-100 flex items-center justify-between gap-2">
								<span>
									Mostrando {operators.length} de {busqueda.total}.
								</span>
								{puedeVerMas ? (
									<button
										type="button"
										onClick={() =>
											setLimite((previo) =>
												Math.min(
													previo + PASO_DE_LIMITE,
													LIMITE_MAXIMO,
												),
											)
										}
										className="text-primary-700 font-semibold whitespace-nowrap"
									>
										Ver más
									</button>
								) : (
									<span>Escribí también el nombre.</span>
								)}
							</div>
						)}
					</CommandList>
					</div>,
					document.body,
				)}
			</Command>
			{/*
			 * El cajón para dar de alta un operador sólo se dibuja si hay
			 * formulario: `AddOperatorForm` lee el contexto de react-hook-form y
			 * `DrawerContent` necesita un `Drawer` que lo contenga, y ninguna de las
			 * dos cosas existe cuando el combobox se usa suelto. Donde sí existen
			 * —las dos instancias del alta de operación— se dibuja igual que antes.
			 */}
			{operationForm && (
				<DrawerContent className="h-[97%] rounded-t-[48px] px-8 sm:px-12">
					<DrawerHeader>
						<div className="flex flex-col mb-10 mt-6">
							<div className="flex flex-row w-full align-center items-center justify-between mb-1">
								<DrawerTitle asChild>
									<>
										<Title
											hierarchy="h2"
											color="text-primary-800"
											weight="font-semibold"
											className="text-center m-0 text-big"
										>
											Agregar Operador
										</Title>
									</>
								</DrawerTitle>
								<DrawerClose>
									<Icon
										name="close"
										size="size-6"
										color="text-grey-500"
									/>
								</DrawerClose>
							</div>
							<span className="text-grey-500">
								{" "}
								El asterisco indica que es obligatorio *{" "}
							</span>
						</div>
					</DrawerHeader>
					<DrawerDescription asChild>
						<AddOperatorForm
							bases={bases}
							onSubmit={onSubmitNewOperator}
						/>
					</DrawerDescription>
					<DrawerFooter className="sticky top-auto bottom-0 w-full justify-center flex items-center flex-col gap-4">
						{operationForm.formState.errors.root?.message && (
							<div className="w-full max-w-96 bg-red-50 border-2 border-red-400 rounded-lg p-4 flex items-start gap-3">
								<Icon
									name="warning"
									size="size-6"
									color="text-error-600"
									className="flex-shrink-0 mt-0.5"
								/>
								<div className="flex flex-col gap-1">
									<span className="font-semibold text-error-800 text-sm">
										Error al agregar operador
									</span>
									<span className="text-error-700 text-sm">
										{operationForm.formState.errors.root.message}
									</span>
								</div>
							</div>
						)}
						<Button
							type="submit"
							form="addOperatorForm"
							className="w-full p-2 bg-blue-500 text-white rounded mb-10 max-w-96"
							loading={isSubmitting}
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Agregando...' : 'Agregar operador'}
						</Button>
					</DrawerFooter>
				</DrawerContent>
			)}
			{error && <p className="text-red-500">{error}</p>}
		</div>
	);
}
